import React, { useRef, useEffect, useState, useLayoutEffect } from "react";
import styles from "./Tooltip.module.scss";
import classNames from "classnames";
import ReactDOM from "react-dom";

let active = null;
export const closeActivePopover = () => {
    if (active) {
        active.forceClose();
        active = null;
    }
}

export const setActivePopover = ({ triggerRef, popoverRef, forceClose }) => {
    if (active && active.ref !== popoverRef) {
        closeActivePopover();
    }

    active = {
        popoverRef,
        triggerRef,
        forceClose
    }
}

/**
    horizontalPlacement - left (default), center, right
    verticalPlacement - top, center, bottom (default)
 */
const Popover = ({ children, triggerRef, setIsHovered, horizontalPlacement, verticalPlacement, calculatePositionFromParent, forceClose, className, style }) => {
    const popoverRef = useRef();
    const [isEntering, setIsEntering] = useState(true);

    useLayoutEffect(() => {
        if (!triggerRef || !triggerRef.current) {
            return;
        }

        const boundNode = calculatePositionFromParent ? triggerRef.current.parentNode : triggerRef.current;
        const boundingRect = boundNode.getBoundingClientRect();
        let placementHeight = boundingRect.height;
        let placementWidth = 0;

        if (verticalPlacement === "center") {
            placementHeight = boundingRect.height / 2;
        } else if (verticalPlacement === 'top') {
            placementHeight = 0;
        }

        if(horizontalPlacement === "center") {
            placementWidth = boundingRect.width / 2;
        } else if (horizontalPlacement === "right") {
            placementWidth = boundingRect.width;
        }

        popoverRef.current.style.top = `${boundingRect.top + placementHeight}px`;
        popoverRef.current.style.left = `${boundingRect.left + placementWidth}px`;
    }, []);

    useEffect(() => {
        setActivePopover({ triggerRef, popoverRef, forceClose });
        setTimeout(() => {
            if(popoverRef === active?.popoverRef) {
                setIsEntering(false);
            }
        }, 50);

        return () => {
            if (popoverRef === active?.popoverRef) {
                closeActivePopover();
            }
        }
    }, []);

    return ReactDOM.createPortal(<div className={classNames(styles.popover, className)} ref={popoverRef} onMouseOver={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} style={{...style, opacity: isEntering ? 0 : 1}}>{children}</div>, document.body)
}

export const Render = React.memo(({ children, triggerRef, isTriggerHovered, calculatePositionFromParent, verticalPlacement, horizontalPlacement, className, style, onIsShowing }) => {
    const [isPopoverHovered, setIsPopoverHovered] = useState(false);
    const [isShowing, setIsShowing] = useState(false);
    const timeoutRef = useRef(null);

    useEffect(() => {
        if (isTriggerHovered || isPopoverHovered) {
            onIsShowing && onIsShowing(true);
            setIsShowing(true);
        } else {
            timeoutRef.current = setTimeout(() => {
                onIsShowing && onIsShowing(false);
                setIsShowing(false);
            }, 250);
        }

        return () => {
            timeoutRef.current && clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, [isTriggerHovered, isPopoverHovered, onIsShowing]);

    if (isShowing) {
        return <Popover triggerRef={triggerRef} isHovered={isPopoverHovered} setIsHovered={setIsPopoverHovered} calculatePositionFromParent={calculatePositionFromParent} verticalPlacement={verticalPlacement} horizontalPlacement={horizontalPlacement} className={className} style={style} forceClose={() => {
            setIsShowing(false)
            onIsShowing && onIsShowing(false);
            timeoutRef.current && clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }}>{children}</Popover>
    } else {
        return ""
    }
});

export const TooltipContent = React.memo(({ children, className, style }) => {
    return <div className={classNames(styles.content, className)} style={style}>
        {children}
    </div>
})

export const TooltipListContent = React.memo(({ children, className, style }) => {
    return <div className={classNames(styles["list-content"], className)} style={style}>
        {children}
    </div>
})

const Tooltip = React.memo(({ children, className, style, placement, contentClassName, contentStyle, Content = TooltipContent }) => {
    const ref = useRef(null);
    const [isHovered, setHovered] = useState(false);

    useEffect(() => {
        if (ref.current) {
            const observer = new ResizeObserver((entries) => {
                if (ref.current?.isConnected && entries[0].contentRect.width === 1) {
                    setHovered(true);
                } else {
                    setHovered(false);
                }
            })
            observer.observe(ref.current)
            return () => observer.disconnect();
        }
    }, []);

    return <div className={styles.listener} ref={ref}>
        <Render className={classNames(styles.tooltip, className)} style={style} triggerRef={ref} calculatePositionFromParent={true} isTriggerHovered={isHovered} verticalPlacement={placement}>
            <Content triggerRef={ref} className={contentClassName} style={contentStyle}>{children}</Content>
        </Render>
    </div>
})

export default Tooltip